import { useState, useEffect } from 'react';
import { api, ProductRequestWithDetails, ProductRequestStatus } from '../api';
import Layout from '../components/Layout';

const STATUS_INFO: Record<ProductRequestStatus, { label: string; color: string; description: string }> = {
  submitted: { label: 'Submitted', color: 'bg-blue-500/20 text-blue-400', description: 'Your request has been submitted and is awaiting review.' },
  in_review: { label: 'In Review', color: 'bg-yellow-500/20 text-yellow-400', description: 'Our team is reviewing your request.' },
  design_in_progress: { label: 'Design In Progress', color: 'bg-purple-500/20 text-purple-400', description: 'Design work is underway for your product.' },
  waiting_on_vendor: { label: 'Waiting on Vendor', color: 'bg-orange-500/20 text-orange-400', description: 'We\'re waiting for vendor confirmation.' },
  approved_to_create_shopify: { label: 'Approved', color: 'bg-emerald-500/20 text-emerald-400', description: 'Your product has been approved and will be created soon!' },
  live: { label: 'Live', color: 'bg-green-500/20 text-green-400', description: 'Your product is now live in your store!' },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400', description: 'Unfortunately, this request was not approved.' },
  cancelled: { label: 'Cancelled', color: 'bg-ink-600/50 text-ink-400', description: 'This request has been cancelled.' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function InfluencerRequests() {
  const [requests, setRequests] = useState<ProductRequestWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const { requests } = await api.getMyProductRequests();
      setRequests(requests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">My Requests</h1>
          <p className="text-ink-400 mt-1">Track the status of your product requests</p>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Requests List */}
        {requests.length === 0 ? (
          <div className="card p-12 text-center">
            <svg
              className="w-16 h-16 text-ink-600 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <h3 className="text-lg font-medium text-ink-200 mb-2">No requests yet</h3>
            <p className="text-ink-400 mb-4">
              Browse our catalog to request products for your store.
            </p>
            <a href="/explore" className="btn-primary inline-flex">
              Explore Products
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const statusInfo = STATUS_INFO[req.status];
              return (
                <div key={req.id} className="card p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-medium text-ink-100">{req.product_title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="text-sm text-ink-400 mb-3">{statusInfo.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-ink-300">
                        <div>
                          <span className="text-ink-500">Requested:</span>{' '}
                          {formatDate(req.created_at)}
                        </div>
                        {req.requested_retail && (
                          <div>
                            <span className="text-ink-500">Price:</span>{' '}
                            ${req.requested_retail.toFixed(2)}
                          </div>
                        )}
                        {req.wants_designer ? (
                          <div className="text-purple-400">
                            <svg
                              className="w-4 h-4 inline mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                              />
                            </svg>
                            Design help requested
                          </div>
                        ) : null}
                      </div>

                      {req.notes && (
                        <div className="mt-3 p-3 bg-ink-800 rounded-lg text-sm text-ink-300">
                          <span className="text-ink-500 font-medium">Your notes:</span> {req.notes}
                        </div>
                      )}

                      {req.requested_customization && (
                        <div className="mt-2 p-3 bg-ink-800 rounded-lg text-sm text-ink-300">
                          <span className="text-ink-500 font-medium">Customization:</span>{' '}
                          {req.requested_customization}
                        </div>
                      )}
                    </div>

                    {/* Timeline indicator */}
                    <div className="hidden md:block">
                      <div className="w-24 h-24 rounded-full bg-ink-800 flex items-center justify-center">
                        {req.status === 'live' ? (
                          <svg
                            className="w-10 h-10 text-green-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : req.status === 'rejected' || req.status === 'cancelled' ? (
                          <svg
                            className="w-10 h-10 text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-10 h-10 text-accent-400 animate-pulse"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
